import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, CheckCircle, XCircle, Loader2 } from 'lucide-react';

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState(null);

  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('No invitation token provided');
      setLoading(false);
      return;
    }

    verifyToken();
  }, [token]);

  const verifyToken = async () => {
    try {
      const response = await fetch(`/api/users/invitations/verify?token=${token}`);
      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Invalid invitation');
        setLoading(false);
        return;
      }

      setInvitation(data);
      // Pre-fill display name from email
      const emailName = data.email.split('@')[0];
      setDisplayName(emailName.charAt(0).toUpperCase() + emailName.slice(1));
    } catch (err) {
      setError('Failed to verify invitation');
    } finally {
      setLoading(false);
    }
  };

  const validatePassword = () => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters';
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain an uppercase letter';
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain a lowercase letter';
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain a number';
    }
    if (password !== confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!username.trim()) {
      setError('Username is required');
      return;
    }

    const passwordError = validatePassword();
    if (passwordError) {
      setError(passwordError);
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch('/api/users/invitations/complete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',  // Include cookies for httpOnly auth
        body: JSON.stringify({
          token,
          username: username.trim(),
          password,
          display_name: displayName.trim() || username.trim()
        })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.detail || 'Failed to create account');
        setSubmitting(false);
        return;
      }

      // Token is set as httpOnly cookie by the server
      // Keep localStorage for backward compatibility
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/');
    } catch (err) {
      setError('Failed to create account');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-virgo-teal-400 animate-spin mx-auto" />
          <p className="text-virgo-teal-300 mt-4">Verifying invitation...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
        <div className="bg-virgo-navy-700 rounded-xl p-8 max-w-md w-full text-center border border-virgo-navy-600">
          <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Invalid Invitation</h1>
          <p className="text-virgo-teal-300 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-virgo-teal-600 text-white px-6 py-2 rounded-lg hover:bg-virgo-teal-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center p-4">
      <div className="bg-virgo-navy-700 rounded-xl p-8 max-w-md w-full border border-virgo-navy-600">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-virgo-teal-500 to-virgo-teal-600 rounded-xl flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-virgo-gold-400">Welcome to Virgo</h1>
          <p className="text-virgo-teal-300 mt-2">Complete your account setup</p>
        </div>

        {/* Invitation Details */}
        <div className="bg-virgo-navy-800 rounded-lg p-4 mb-6 border border-virgo-navy-600">
          <div className="flex items-center gap-3 mb-3">
            <Mail className="w-5 h-5 text-virgo-teal-400" />
            <div>
              <p className="text-xs text-virgo-teal-400">Email</p>
              <p className="text-white">{invitation?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-virgo-teal-400" />
            <div>
              <p className="text-xs text-virgo-teal-400">Role</p>
              <p className="text-white capitalize">{invitation?.role}</p>
            </div>
          </div>
          {invitation?.invited_by && (
            <p className="text-virgo-teal-300 text-sm mt-3">
              Invited by {invitation.invited_by}
            </p>
          )}
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Username */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Username
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg pl-10 pr-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                placeholder="Choose a username"
                required
              />
            </div>
          </div>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
              placeholder="Your name"
            />
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg pl-10 pr-12 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                placeholder="Create a password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-virgo-teal-500 hover:text-virgo-teal-400"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-virgo-teal-400 mb-1">
              Confirm Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-virgo-teal-500" />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-virgo-navy-800 border border-virgo-navy-600 rounded-lg pl-10 pr-12 py-2 text-white focus:outline-none focus:ring-2 focus:ring-virgo-teal-500"
                placeholder="Confirm your password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-virgo-teal-500 hover:text-virgo-teal-400"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Password Requirements */}
          <div className="text-xs text-virgo-teal-400 bg-virgo-navy-800 p-3 rounded-lg">
            Password must have: 8+ characters, uppercase, lowercase, and a number
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-gradient-to-r from-virgo-teal-500 to-virgo-teal-600 text-white py-3 rounded-lg hover:from-virgo-teal-600 hover:to-virgo-teal-700 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Creating Account...
              </>
            ) : (
              'Create Account'
            )}
          </button>
        </form>

        <p className="text-center text-virgo-teal-400 text-sm mt-6">
          Already have an account?{' '}
          <a href="/login" className="text-virgo-teal-300 hover:text-white">
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}

export default AcceptInvite;
